import apiClient from './client'

// Severity counts type
export interface VulnerabilitySeverityCounts {
  critical: number
  high: number
  medium: number
  low: number
  total: number
}

// Summary response
export interface AnalyticsSummary {
  total_services: number
  total_scans: number
  total_vulnerabilities: number
  vulnerabilities_by_severity: VulnerabilitySeverityCounts
  services_with_critical: number
  services_with_high: number
  average_vulnerabilities_per_scan: number
}

// Trend data point
export interface TrendDataPoint {
  date: string // ISO date string (YYYY-MM-DD)
  critical: number
  high: number
  medium: number
  low: number
  total: number
  scans: number
}

// Trends response
export interface AnalyticsTrends {
  period: string // "7d" or "30d"
  data: TrendDataPoint[]
  change_from_previous: VulnerabilitySeverityCounts
}

// Vulnerability distribution response
export interface VulnerabilityDistribution {
  by_severity: VulnerabilitySeverityCounts
  by_ecosystem: Record<string, number>
  top_packages: Array<{
    package: string
    count: number
    severity: string
  }>
}

// Service analytics response
export interface ServiceAnalytics {
  service_id: string
  service_name: string
  total_scans: number
  total_vulnerabilities: number
  vulnerabilities_by_severity: VulnerabilitySeverityCounts
  health_score: number
  trends: TrendDataPoint[]
  top_vulnerabilities: Array<{
    vulnerability_id: string
    name: string
    severity: string
    count: number
  }>
}

export const analyticsApi = {
  /**
   * Get overview analytics summary for the current user
   */
  getSummary: async (): Promise<AnalyticsSummary> => {
    const response = await apiClient.get<AnalyticsSummary>('/analytics/summary')
    return response.data
  },

  /**
   * Get historical vulnerability trends
   */
  getTrends: async (days: number = 30): Promise<AnalyticsTrends> => {
    const response = await apiClient.get<AnalyticsTrends>('/analytics/trends', {
      params: { days },
    })
    return response.data
  },

  /**
   * Get vulnerability distribution by severity, ecosystem, and top packages
   */
  getVulnerabilityDistribution: async (): Promise<VulnerabilityDistribution> => {
    const response = await apiClient.get<VulnerabilityDistribution>('/analytics/vulnerabilities')
    return response.data
  },

  /**
   * Get detailed analytics for a specific service
   */
  getServiceAnalytics: async (serviceId: string): Promise<ServiceAnalytics> => {
    const response = await apiClient.get<ServiceAnalytics>(`/analytics/services/${serviceId}`)
    return response.data
  },

  // Performance Analytics (Phase 7)
  getResponseTimeTrends: async (
    appId: string,
    days: number = 30,
    granularity: string = 'hour'
  ): Promise<import('../types').ResponseTimeTrend[]> => {
    const response = await apiClient.get<import('../types').ResponseTimeTrend[]>(
      `/analytics/response-time-trends/${appId}`,
      { params: { days, granularity } }
    )
    return response.data
  },

  getErrorRateTrends: async (
    appId: string,
    days: number = 30,
    granularity: string = 'day'
  ): Promise<import('../types').ErrorRateTrend[]> => {
    const response = await apiClient.get<import('../types').ErrorRateTrend[]>(
      `/analytics/error-rate-trends/${appId}`,
      { params: { days, granularity } }
    )
    return response.data
  },

  getUptimeAnalytics: async (
    appId: string,
    days: number = 30
  ): Promise<import('../types').UptimeAnalytics> => {
    const response = await apiClient.get<import('../types').UptimeAnalytics>(
      `/analytics/uptime/${appId}`,
      { params: { days } }
    )
    return response.data
  },

  getUptimeTrends: async (
    appId: string,
    days: number = 30,
    granularity: string = 'day'
  ): Promise<import('../types').UptimeTrend[]> => {
    const response = await apiClient.get<import('../types').UptimeTrend[]>(
      `/analytics/uptime-trends/${appId}`,
      { params: { days, granularity } }
    )
    return response.data
  },

  getDashboardSummary: async (): Promise<import('../types').DashboardSummary> => {
    const response = await apiClient.get<import('../types').DashboardSummary>(
      '/analytics/dashboard-summary'
    )
    return response.data
  },

  getMetricTrends: async (
    appId: string,
    metricName: string,
    hours: number = 24
  ): Promise<import('../types').MetricTrendsResponse> => {
    const response = await apiClient.get<import('../types').MetricTrendsResponse>(
      `/analytics/metrics-trends/${appId}`,
      { params: { metric_name: metricName, hours } }
    )
    return response.data
  },

  // Anomaly Detection
  calculateBaseline: async (
    appId: string,
    metricName: string,
    periodDays: number = 30
  ): Promise<import('../types').PerformanceBaseline> => {
    const response = await apiClient.post<import('../types').PerformanceBaseline>(
      `/analytics/baselines/${appId}/${metricName}`,
      null,
      { params: { period_days: periodDays } }
    )
    return response.data
  },

  getAnomalies: async (
    appId?: string,
    days: number = 7,
    acknowledged?: boolean
  ): Promise<import('../types').AnomaliesListResponse> => {
    const params: Record<string, unknown> = { days }
    if (appId) params.app_id = appId
    if (acknowledged !== undefined) params.acknowledged = acknowledged

    const response = await apiClient.get<import('../types').AnomaliesListResponse>(
      '/analytics/anomalies',
      { params }
    )
    return response.data
  },

  acknowledgeAnomaly: async (anomalyId: string): Promise<void> => {
    await apiClient.post(`/analytics/anomalies/${anomalyId}/acknowledge`)
  },

  getAnomalyContext: async (anomalyId: string): Promise<import('../types').AnomalyContext> => {
    const response = await apiClient.get<import('../types').AnomalyContext>(
      `/analytics/anomalies/${anomalyId}/context`
    )
    return response.data
  },

  // Scheduled Reports
  createScheduledReport: async (
    data: import('../types').ScheduledReportCreate
  ): Promise<import('../types').ScheduledReport> => {
    const response = await apiClient.post<import('../types').ScheduledReport>(
      '/analytics/scheduled-reports',
      data
    )
    return response.data
  },

  listScheduledReports: async (): Promise<import('../types').ScheduledReportsListResponse> => {
    const response = await apiClient.get<import('../types').ScheduledReportsListResponse>(
      '/analytics/scheduled-reports'
    )
    return response.data
  },

  updateScheduledReport: async (
    id: string,
    data: import('../types').ScheduledReportUpdate
  ): Promise<import('../types').ScheduledReport> => {
    const response = await apiClient.patch<import('../types').ScheduledReport>(
      `/analytics/scheduled-reports/${id}`,
      data
    )
    return response.data
  },

  deleteScheduledReport: async (id: string): Promise<void> => {
    await apiClient.delete(`/analytics/scheduled-reports/${id}`)
  },

  getReportHistory: async (
    limit: number = 50
  ): Promise<import('../types').ReportHistoryListResponse> => {
    const response = await apiClient.get<import('../types').ReportHistoryListResponse>(
      '/analytics/report-history',
      { params: { limit } }
    )
    return response.data
  },

  // SLA Reports
  generateSLAReport: async (
    appId: string,
    startDate: string,
    endDate: string
  ): Promise<import('../types').SLAReport> => {
    const response = await apiClient.post<import('../types').SLAReport>('/analytics/sla-report', {
      app_id: appId,
      start_date: startDate,
      end_date: endDate,
    })
    return response.data
  },
}
